import React from "react";
import { Drawer, Box, Typography, IconButton } from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

/**
 * "What's New" announcements - each entry gets its own gradient icon tile (no real screenshots
 * yet, so a bold icon-on-gradient card reads as deliberate design rather than a missing image)
 * plus a title/description/link. New entries go at the top of this array.
 *
 * Bumping this list's length also bumps the unseen "new" dot on the sparkle icon in TopBar
 * (WHATS_NEW_FEATURE_COUNT there must be kept equal to FEATURES.length) - every entry added here
 * re-surfaces the dot for everyone, even people who already dismissed it once.
 */
const FEATURES = [
  {
    key: "app-tour",
    title: "App Tour",
    date: "October 2026",
    description:
      "A guided walkthrough of the whole app - adding a wallet, re-syncing it, transactions, tax, performance and write-offs. New accounts see it automatically; replay it anytime from here.",
    icon: <RocketLaunchIcon style={{ fontSize: 28 }} />,
    gradient: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
    action: "startTour",
    linkLabel: "Take the tour",
  },
  {
    key: "tax-write-offs",
    title: "Tax Write-offs",
    date: "October 2026",
    description:
      "See every asset you're currently holding at a loss, with the potential tax write-off for each one. Mark a position Complete once you've sold it, or Dismiss it if you're holding.",
    icon: <TrendingDownIcon style={{ fontSize: 28 }} />,
    gradient: "linear-gradient(135deg, #22c55e 0%, #0ea5e9 100%)",
    href: "/secure/tax-write-offs",
    linkLabel: "Open Tax Write-offs",
  },
  {
    key: "wallet-sync-progress",
    title: "Live Wallet Sync Progress",
    date: "September 2026",
    description:
      "Watch a real percentage - not just a spinner - while your wallets sync, both on the Wallets page and as a small badge next to Wallets in the nav bar from anywhere in the app.",
    icon: <DonutLargeIcon style={{ fontSize: 28 }} />,
    gradient: "linear-gradient(135deg, rgb(95,81,222) 0%, rgb(173,93,211) 100%)",
    href: "/secure/wallet",
    linkLabel: "Open Wallets",
  },
];

const WhatsNewDrawer = ({ open, onClose, isLightTheme, onStartTour }) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          width: 380,
          maxWidth: "100vw",
          backgroundColor: isLightTheme ? "#FAFAFB" : "#15131C",
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{
          padding: "20px 20px 16px 20px",
          borderBottom: isLightTheme ? "1px solid #eee" : "1px solid #2C2A36",
        }}
      >
        <Typography
          variant="h6"
          style={{ fontFamily: '"Manrope", Helvetica', fontWeight: 700, color: isLightTheme ? "#000" : "#fff" }}
        >
          What's New
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" style={{ color: isLightTheme ? "#555" : "#ccc" }} />
        </IconButton>
      </Box>

      <Box style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        {onStartTour && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
              borderRadius: 14,
              padding: "14px 16px",
              background: isLightTheme
                ? "linear-gradient(135deg, rgba(95,81,222,0.08) 0%, rgba(173,93,211,0.08) 100%)"
                : "linear-gradient(135deg, rgba(95,81,222,0.18) 0%, rgba(173,93,211,0.18) 100%)",
              border: isLightTheme ? "1px solid rgba(95,81,222,0.15)" : "1px solid rgba(173,93,211,0.25)",
              gap: 12,
            }}
          >
            <Box>
              <Typography variant="body2" style={{ fontWeight: 700, color: isLightTheme ? "#111" : "#fff" }}>
                New here?
              </Typography>
              <Typography variant="caption" style={{ color: isLightTheme ? "#666" : "#aaa" }}>
                A 1-minute tour of the whole app
              </Typography>
            </Box>
            <button
              type="button"
              onClick={onStartTour}
              style={{
                border: "none",
                background: "linear-gradient(180deg, rgb(95,81,222) 0%, rgb(173,93,211) 100%)",
                color: "#fff",
                borderRadius: 10,
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Take the tour
            </button>
          </Box>
        )}
        {FEATURES.map((feature) => (
          <Box
            key={feature.key}
            style={{
              borderRadius: 16,
              overflow: "hidden",
              backgroundColor: isLightTheme ? "#fff" : "#1C1825",
              border: isLightTheme ? "1px solid #eee" : "1px solid #2C2A36",
              boxShadow: isLightTheme ? "0px 2px 10px rgba(0,0,0,0.04)" : "none",
            }}
          >
            <Box
              style={{
                height: 88,
                background: feature.gradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(4px)",
                }}
              >
                {feature.icon}
              </Box>
            </Box>

            <Box style={{ padding: "16px 18px 18px 18px" }}>
              <Typography
                variant="caption"
                style={{ color: isLightTheme ? "#999" : "#888", fontWeight: 600, letterSpacing: 0.4 }}
              >
                {feature.date.toUpperCase()}
              </Typography>
              <Typography
                variant="body1"
                style={{
                  fontWeight: 700,
                  color: isLightTheme ? "#111" : "#fff",
                  marginTop: 4,
                  marginBottom: 6,
                }}
              >
                {feature.title}
              </Typography>
              <Typography
                variant="body2"
                style={{ color: isLightTheme ? "#555" : "#bbb", lineHeight: 1.5, marginBottom: 14 }}
              >
                {feature.description}
              </Typography>
              {feature.action === "startTour" ? (
                <button
                  type="button"
                  onClick={() => (onStartTour ? onStartTour() : onClose())}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#7D56E7",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  {feature.linkLabel} &rarr;
                </button>
              ) : (
                <a
                  href={feature.href}
                  onClick={onClose}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#7D56E7",
                    textDecoration: "none",
                  }}
                >
                  {feature.linkLabel} &rarr;
                </a>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Drawer>
  );
};

export default WhatsNewDrawer;
